import type { ReactNode } from 'react';
import { PrimaryButton } from '@/shared/components/ui/PrimaryButton';

export interface ErrorStateProps {
  title?: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  retryLabel?: string;
  onRetry?: () => void;
  secondaryAction?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

function ErrorStateIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.3 3.9 2.6 17.2A1.9 1.9 0 0 0 4.3 20h15.4a1.9 1.9 0 0 0 1.6-2.8L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z" />
    </svg>
  );
}

export function ErrorState({
  title = 'Не удалось загрузить данные',
  description = 'Попробуйте повторить запрос или вернуться к просмотру позже.',
  eyebrow = 'Нужна перезагрузка',
  retryLabel = 'Повторить',
  onRetry,
  secondaryAction,
  children,
  icon,
  className,
}: ErrorStateProps) {
  return (
    <section
      className={`rounded-[28px] border px-5 py-6 shadow-[0_18px_40px_rgba(26,26,26,0.05)] ${className ?? ''}`}
      style={{
        background: 'rgba(255, 248, 231, 0.96)',
        borderColor: 'rgba(232, 93, 42, 0.18)',
      }}
      role="alert"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[20px] bg-[#FBE5DD] text-[#E85D2A]">
          {icon ?? <ErrorStateIcon />}
        </div>

        <div className="min-w-0 flex-1">
          {eyebrow ? (
            <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[#A36444]">
              {eyebrow}
            </p>
          ) : null}
          <h2 className="mt-1 text-lg font-semibold leading-6 text-[#1A1A1A]">{title}</h2>
          {description ? (
            <p className="mt-2 text-sm leading-6 text-[#6B6B6B]">{description}</p>
          ) : null}
        </div>
      </div>

      {children ? <div className="mt-4 text-sm leading-6 text-[#6B6B6B]">{children}</div> : null}

      {(onRetry || secondaryAction) ? (
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          {onRetry ? (
            <PrimaryButton onClick={onRetry} className="min-w-[9rem]">
              {retryLabel}
            </PrimaryButton>
          ) : null}
          {secondaryAction ? <div>{secondaryAction}</div> : null}
        </div>
      ) : null}
    </section>
  );
}
